import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Popconfirm, Radio, Typography } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "../../../../../components/ActionTooltip.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"
import { useTemplateBlocks } from "../../../hooks/use-template-blocks.ts"
import {
  PARTY_TYPES,
  type PartyType,
  type SignerSlot,
  blocksWithSlotRemoved,
  blocksWithSlotRenamed,
  declaredSlots,
  isSignatureBlock,
  mintSlotKey,
  slotOf
} from "../../../signature-slots/signature-slots.ts"
import type {
  DocumentTemplateBlock,
  DocumentTemplateMutateData
} from "../../../types/types.ts"

const { Text } = Typography

// Named once because both of the panel's returns are the same surface.
const PANEL_SURFACE_CLASS = "p-3 bg-slate-50 rounded-lg border border-slate-200"

// Shared by the header row and every signer row so the two line up: the rows
// scroll sideways rather than reflowing away from the header labelling them.
const ROWS_MIN_WIDTH_CLASS = "min-w-[28rem]"
const NAME_COLUMN_CLASS = "min-w-[9rem] flex-1"
const PARTY_TYPE_COLUMN_CLASS = "w-[17rem]"
const ACTION_COLUMN_CLASS = "w-8"

interface SignersPanelProps {
  form: FormInstance<DocumentTemplateMutateData>
  disabled?: boolean
}

/**
 * The only place a signer slot is created, renamed or removed. A slot exists
 * only as a signature block's `config.slot`, so adding a signer means minting
 * one onto the first signature block that declares none.
 */
export function SignersPanel({ form, disabled }: SignersPanelProps) {
  const { t } = useTranslation()
  const blocks = useTemplateBlocks(form)

  const slots = declaredSlots(blocks)
  const hasSignatureBlock = blocks.some((block) => isSignatureBlock(block))
  const freeBlock = blocks.find(
    (block) => isSignatureBlock(block) && !slotOf(block)
  )

  const writeBlocks = (next: DocumentTemplateBlock[]) => {
    form.setFields([{ name: "blocks", touched: true, value: next }])
  }

  // Count past the names in use, not off `slots.length`: removing "Signer 1" of
  // two would otherwise hand the next signer "Signer 2" a second time.
  const nextDefaultName = () => {
    const taken = new Set(slots.map((slot) => slot.name))
    for (let count = 1; ; count += 1) {
      const name = t("documentTemplates.signers.defaultName", { count })
      if (!taken.has(name)) return name
    }
  }

  const handleAdd = () => {
    if (!freeBlock) return

    const name = nextDefaultName()
    const key = mintSlotKey(
      name,
      slots.map((slot) => slot.key)
    )
    const newSlot: SignerSlot = { key, name, party_type: PARTY_TYPES[0] }

    writeBlocks(
      blocks.map((block) =>
        block.id === freeBlock.id
          ? { ...block, config: { ...block.config, slot: newSlot } }
          : block
      )
    )
  }

  const handleRename = (key: string, name: string) => {
    writeBlocks(blocksWithSlotRenamed(blocks, key, { name }))
  }

  const handlePartyTypeChange = (key: string, party_type: PartyType) => {
    writeBlocks(blocksWithSlotRenamed(blocks, key, { party_type }))
  }

  const handleRemove = (key: string) => {
    writeBlocks(blocksWithSlotRemoved(blocks, key))
  }

  const blockCountFor = (key: string) =>
    blocks.filter((block) => slotOf(block)?.key === key).length

  if (!hasSignatureBlock) {
    return (
      <Flex vertical gap={4} className={PANEL_SURFACE_CLASS}>
        <Text strong>{t("documentTemplates.signers.panelTitle")}</Text>
        <Text type="secondary">
          {t("documentTemplates.signers.noSignatureBlock")}
        </Text>
      </Flex>
    )
  }

  const addButton = (
    <Button
      type="dashed"
      size="small"
      icon={<PlusOutlined />}
      disabled={disabled || !freeBlock}
      onClick={handleAdd}
    >
      {t("documentTemplates.signers.addButton")}
    </Button>
  )

  return (
    <Flex vertical gap={8} className={PANEL_SURFACE_CLASS}>
      <Flex justify="space-between" align="center">
        <Text strong>{t("documentTemplates.signers.panelTitle")}</Text>
        {/* A disabled button leaves the tab order, so the tooltip is a
            mouse-only convenience and the hint below is always rendered. */}
        {!disabled && !freeBlock ? (
          <ActionTooltip title={t("documentTemplates.signers.addDisabledHint")}>
            <span>{addButton}</span>
          </ActionTooltip>
        ) : (
          addButton
        )}
      </Flex>

      {!disabled && !freeBlock && (
        <Text type="secondary">
          {t("documentTemplates.signers.addDisabledHint")}
        </Text>
      )}

      {slots.length === 0 ? (
        <Text type="secondary">{t("documentTemplates.signers.empty")}</Text>
      ) : (
        <div className="overflow-x-auto tailwind-scroll">
          <Flex vertical gap={8} className={ROWS_MIN_WIDTH_CLASS}>
            <Flex gap={8} align="center">
              <Text type="secondary" className={NAME_COLUMN_CLASS}>
                {t("documentTemplates.signers.nameLabel")}
                <span aria-hidden className="ml-0.5 text-red-500">
                  *
                </span>
              </Text>
              <Text type="secondary" className={PARTY_TYPE_COLUMN_CLASS}>
                {t("documentTemplates.signers.partyTypeLabel")}
              </Text>
              <span className={ACTION_COLUMN_CLASS} />
            </Flex>

            {slots.map((slot) => {
              const count = blockCountFor(slot.key)
              return (
                <Flex key={slot.key} gap={8} align="center">
                  <TextInput
                    aria-label={t("documentTemplates.signers.nameLabel")}
                    aria-required
                    value={slot.name}
                    disabled={disabled}
                    className={NAME_COLUMN_CLASS}
                    onChange={(e) => handleRename(slot.key, e.target.value)}
                  />
                  <Radio.Group
                    // One native radio group per row: an inherited name is a
                    // single group, and checking one row would clear another.
                    name={`party-type-${slot.key}`}
                    aria-label={t("documentTemplates.signers.partyTypeLabel")}
                    className={PARTY_TYPE_COLUMN_CLASS}
                    value={slot.party_type}
                    disabled={disabled}
                    options={PARTY_TYPES.map((type) => ({
                      value: type,
                      label: t(`documentTemplates.signers.partyType.${type}`)
                    }))}
                    onChange={(e) =>
                      handlePartyTypeChange(
                        slot.key,
                        e.target.value as PartyType
                      )
                    }
                  />
                  {/* Outside the Popconfirm, not inside it: `ActionTooltip`
                      keeps only `title`/`placement`/`children`, so it would
                      drop the Popconfirm's own `onClick` and the confirm
                      would never open. */}
                  <ActionTooltip
                    title={t("documentTemplates.signers.removeButtonLabel")}
                  >
                    <Popconfirm
                      okType="danger"
                      title={t("documentTemplates.signers.removeConfirmTitle", {
                        name: slot.name
                      })}
                      description={t(
                        "documentTemplates.signers.removeConfirm",
                        { count }
                      )}
                      okText={t("documentTemplates.signers.removeConfirmOk")}
                      cancelText={t(
                        "documentTemplates.signers.removeConfirmCancel"
                      )}
                      onConfirm={() => handleRemove(slot.key)}
                    >
                      <Button
                        aria-label={t(
                          "documentTemplates.signers.removeButtonLabel"
                        )}
                        danger
                        type="text"
                        disabled={disabled}
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  </ActionTooltip>
                </Flex>
              )
            })}
          </Flex>
        </div>
      )}
    </Flex>
  )
}
